import { cn } from "@/lib/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef } from "react";

/** Classes of `Spinner`: a ring with one transparent or contrasting arc; `game` is the prize games' gold ring. */
export const spinnerVariants = cva("inline-block shrink-0 animate-spin rounded-full", {
  variants: {
    size: {
      xs: "h-4 w-4 border-2",
      sm: "h-5 w-5 border",
      md: "h-9 w-9 border-4",
      lg: "h-16 w-16 border-4",
      xl: "h-20 w-20 border-4",
    },
    tone: {
      current: "border-current border-t-transparent",
      primary: "border-card-text/10 border-t-primary",
      secondary: "border-card-text/10 border-t-secondary",
      "on-media": "border-on-media border-t-transparent",
      game: "border-game-gold-light border-t-transparent",
    },
  },
  defaultVariants: { size: "sm", tone: "current" },
});

/** Props of `Spinner`; `label` announces it (role=status), otherwise it is decorative. */
export type SpinnerProps = VariantProps<typeof spinnerVariants> & {
  label?: string;
  className?: string;
};

/** Indeterminate loading ring. */
export const Spinner = forwardRef<HTMLSpanElement, SpinnerProps>(function Spinner(
  { size, tone, label, className },
  ref,
) {
  return (
    <span
      ref={ref}
      role={label ? "status" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn(spinnerVariants({ size, tone }), className)}
    />
  );
});
